import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../components/toasts';
import { TeamBadge } from '../../components/TeamBadge';
import { Badge, Button, ErrorText, Input, Modal } from '../../components/ui';
import { post } from '../../lib/api';
import { contactLabel, dateTimeOf, formatPhone, statusLabel } from '../../lib/format';
import { useAttendances, useContactHistory } from '../../lib/queries';
import { AttendanceDetails, AttendanceItem } from '../../lib/types';

/**
 * Vínculo manual da ligação (P6): o atendimento aberto pela ligação passa para outro atendimento
 * (do mesmo cliente, ou de outro: cliente que ligou de um número sem cadastro). Vale o tempo todo
 * da ligação; anotações, gravação e a ligação vão junto e este atendimento deixa de existir.
 */
export function LinkCallModal({ attendance, onClose }: { attendance: AttendanceDetails; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const mine = useAttendances('mine').data ?? [];
  const queue = useAttendances('queue').data ?? [];
  const sameContact = useContactHistory(attendance.contactId).data ?? [];
  const [q, setQ] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  // Do mesmo cliente primeiro, depois os meus e os da fila; sem repetir e sem os encerrados.
  const options = useMemo(() => {
    const seen = new Set<string>([attendance.id]);
    const list: Array<AttendanceItem & { group: string }> = [];
    const add = (items: AttendanceItem[], group: string) => {
      for (const i of items) {
        if (seen.has(i.id) || i.status === 'finalizado') continue;
        seen.add(i.id);
        list.push({ ...i, group });
      }
    };
    add(sameContact, 'Deste cliente');
    add(mine, 'Meus atendimentos');
    add(queue, 'Na fila');
    const term = q.trim().toLowerCase();
    const digits = term.replace(/\D/g, '');
    return term
      ? list.filter((i) => contactLabel(i).toLowerCase().includes(term) || (digits.length >= 3 && (i.contactPhone ?? '').includes(digits)))
      : list;
  }, [attendance.id, sameContact, mine, queue, q]);

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    setError(null);
    try {
      await post(`/attendances/${attendance.id}/merge-into`, { attendanceId: target });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      void qc.invalidateQueries({ queryKey: ['attendance', target] });
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['calls'] });
      toast('Ligação vinculada ao atendimento', 'ok');
      onClose();
      navigate(`/atendimento/${target}`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Vincular a outro atendimento"
      onClose={onClose}
      width="max-w-xl"
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!target} loading={saving} onClick={() => void submit()}>
            Vincular
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-fg-soft">
        A ligação passa para o atendimento escolhido, com todo o tempo dela (inclusive o de antes do vínculo), as anotações e a gravação. Este atendimento de
        ligação deixa de existir. Se o atendimento escolhido estiver na fila, você assume; se estiver com outro operador, você entra para ajudar.
      </p>
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-soft" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por nome ou telefone" className="pl-8" autoFocus />
      </div>
      <div className="max-h-80 overflow-y-auto rounded-md border border-border">
        {options.length === 0 && <div className="px-3 py-2 text-sm text-fg-soft">Nenhum atendimento em aberto para vincular.</div>}
        {options.map((o, i) => (
          <div key={o.id}>
            {(i === 0 || options[i - 1].group !== o.group) && <div className="bg-muted px-3 py-1 text-[11px] font-semibold tracking-wide text-fg-soft uppercase">{o.group}</div>}
            <button
              onClick={() => setTarget(o.id)}
              className={clsx('flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-0', target === o.id ? 'bg-accent/10' : 'hover:bg-muted')}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{contactLabel(o)}</div>
                <div className="truncate text-xs text-fg-soft">
                  {o.contactPhone ? formatPhone(o.contactPhone) : ''} · {o.channelName} · desde {dateTimeOf(o.createdAt)}
                  {o.operatorName ? ` · ${o.operatorName}` : ''}
                </div>
              </div>
              {o.teamName && <TeamBadge name={o.teamName} />}
              <Badge>{statusLabel(o)}</Badge>
            </button>
          </div>
        ))}
      </div>
      <ErrorText error={error} />
    </Modal>
  );
}
